import React from 'react';
import ReactDOM from 'react-dom';

import {Permissions} from 'auth/PermissionConstants';
import {User} from 'constants/UserConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import * as MetadataTabAction from 'metadata/MetadataTabReduxActions';
import MetadataTabState from 'metadata/MetadataTabState';
// @ts-expect-error
import * as selutil from 'notebook/mediocre/selection/text/SelUtil';
import notifications from 'notifications';
import {getPartyDisplayString} from 'parties/GetPartyDisplayString';
import {getStore} from 'redux-stores/Store';
import * as RegistryValidatorActions from 'registry/RegistryValidatorActions';
import * as IntegrationFieldUtil from 'schemas/fields/integration/IntegrationFieldUtil';
import {EntityWithText} from 'ui-library/entity-input/EntityInputConstants';
import * as ModalContainer from 'ui-library/ModalContainer';
import Modal from 'ui-library/modals/Modal';
import {withLoadingScreen} from 'util/Promises';
import {OngoingTaskResponse, resumeTask} from 'util/TaskRunner';
import {TextAreaChangeEvent} from 'util/Types';
import * as StageEntryActions from 'workflows/stage-entries/StageEntryActions';

import CreateUpdateReviewModalBody from './CreateUpdateReviewModalBody';
import {Review, ReviewAction} from './ReviewConstants';
import * as ReviewDAO from './ReviewDAO';
import {ReviewUpdatedResponse} from './ReviewDAO';
import * as ReviewUtil from './ReviewUtil';

function openMetadataTabForError(fileId: string, message: string): void {
  FileBrowserActions.openFile(fileId, {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onOpen(fileState: any): void {
      fileState.activateTab(MetadataTabState);
    },
  });
  notifications.warn(message, {id: 'BulkCreateStageEntryReviewModal:openMetadataTabForError'});
}

async function fetchValidation(stageEntryIds: Array<string>): Promise<boolean> {
  const errorsByReviewableId = await RegistryValidatorActions.bulkValidateFileTagsForReview(stageEntryIds);
  for (const stageEntryId of Object.keys(errorsByReviewableId)) {
    if (errorsByReviewableId[stageEntryId].length) {
      IntegrationFieldUtil.sendReviewBlockingAnalytics(errorsByReviewableId[stageEntryId]);
      getStore().dispatch(MetadataTabAction.setReviewBlockedByErrors(stageEntryId, true));
      openMetadataTabForError(stageEntryId, 'Fix schema errors to send for review.');
      return false;
    }
  }
  return true;
}

interface BulkCreateStageEntryReviewModalProps {
  auditors: Array<User>;
  stageEntryIds: Array<string>;
}

interface State {
  error: string;
  submitting: boolean;
  selectedAuditors: Array<EntityWithText>;
  message: string | null;
}

/*
This modal is for creating reviews for all stage entries in a workflow.
It is not used to accept, reject, retract, or re-send reviews that already exist -- that is unsupported.
*/
class BulkCreateStageEntryReviewModal extends React.Component<BulkCreateStageEntryReviewModalProps, State> {
  textareaRef = React.createRef<HTMLTextAreaElement>();
  static close(): void {
    ModalContainer.close(BulkCreateStageEntryReviewModal);
  }
  static async open({
    stageEntryIds,
    workflowId,
  }: {
    stageEntryIds: Array<string>;
    workflowId: string;
  }): Promise<void> {
    const [auditors, isValid] = await withLoadingScreen(
      Promise.all([ReviewDAO.fetchAuditorsForWorkflow(workflowId), fetchValidation(stageEntryIds)])
    );
    if (isValid) {
      ModalContainer.open(
        BulkCreateStageEntryReviewModal,
        <BulkCreateStageEntryReviewModal stageEntryIds={stageEntryIds} auditors={auditors} />
      );
    }
  }

  state: State = {
    error: '',
    submitting: false,
    selectedAuditors: [],
    message: ReviewUtil.getDefaultReviewComment(ReviewAction.SEND_FOR_REVIEW),
  };
  _isMounted = false;

  componentDidMount(): void {
    this._isMounted = true;
    if (this.textareaRef.current) {
      const textarea = ReactDOM.findDOMNode(this.textareaRef.current);
      selutil.restoreSelection(textarea, {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        start: (textarea as any).value.length,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        end: (textarea as any).value.length,
      });
    }

    let defaultAuditors: Array<User> = [];
    if (this.props.auditors.length === 1) {
      // Only set default auditors if there is exactly 1, otherwise it becomes a
      // long list of auditors that the user needs to edit.
      defaultAuditors = this.props.auditors;
    }
    this.setState({selectedAuditors: this._ensureTextField(defaultAuditors)});
  }

  componentWillUnmount(): void {
    this._isMounted = false;
  }

  onReviewUpdate(
    stageEntryId: string,
    review: Review,
    permissions: Permissions,
    snapshotTask: OngoingTaskResponse | null
  ): void {
    StageEntryActions.updateReview(stageEntryId, review, permissions);

    if (snapshotTask) {
      resumeTask<ReviewUpdatedResponse>(snapshotTask.status_url).then((response) => {
        if (ReviewDAO.isReviewUpdatedResponse(response)) {
          const {review: updatedReview, filePermissions} = response;
          StageEntryActions.updateReview(stageEntryId, updatedReview, filePermissions);
        }
      });
    }
  }

  _submitReviewChange(): Promise<{
    reviewUpdatedResponseByReviewableId: {[reviewableId: string]: ReviewUpdatedResponse};
  }> {
    const auditorHandles = this.state.selectedAuditors.map((auditor) => auditor.handle);
    return ReviewDAO.bulkCreateReview({
      auditorHandles,
      message: this.state.message,
      reviewableIds: this.props.stageEntryIds,
      action: ReviewAction.SEND_FOR_REVIEW,
    });
  }

  handleSubmit(): void {
    this.setState({submitting: true});
    this._submitReviewChange()
      .then(
        ({
          reviewUpdatedResponseByReviewableId,
        }: {
          reviewUpdatedResponseByReviewableId: {[reviewableId: string]: ReviewUpdatedResponse};
        }) => {
          notifications.success('Successfully created reviews', {duration: 5});

          for (const stageEntryId of Object.keys(reviewUpdatedResponseByReviewableId)) {
            const data = reviewUpdatedResponseByReviewableId[stageEntryId];
            this.onReviewUpdate(stageEntryId, data.review, data.filePermissions, data.snapshotTask);
          }
          BulkCreateStageEntryReviewModal.close();
        }
      )
      .catch((error) => {
        const friendlyError = notifications.getFriendlyError(error);
        if (friendlyError && this._isMounted) {
          this.setState({error: friendlyError});
        } else {
          notifications.notifyError(error);
        }
      })
      .finally(() => {
        if (this._isMounted) {
          this.setState({submitting: false});
        }
      });
  }

  _ensureTextField(entities: Array<EntityWithText | User>): Array<EntityWithText> {
    return entities.map((entity) => ({
      ...entity,
      text: getPartyDisplayString(entity)!,
    }));
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  handleCommentChange = (e: TextAreaChangeEvent) => {
    this.setState({message: e.target.value});
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  handleAuditorChange = (entities: Array<EntityWithText>) => {
    this.setState({
      selectedAuditors: this._ensureTextField(entities),
    });
  };

  render(): JSX.Element {
    const submitText = this.state.submitting ? 'Creating...' : 'Bulk create reviews';
    const title = 'Bulk create reviews';
    const modalProps = {
      enableRedesign: true,
      title,
      showFooterBorder: false,
      buttons: [{submit: true, text: submitText, disabled: this.state.submitting}],
      closeable: !this.state.submitting,
      onRequestSubmit: () => {
        this.handleSubmit();
      },
      onRequestClose: () => {
        BulkCreateStageEntryReviewModal.close();
      },
    };

    return (
      <Modal {...modalProps}>
        <CreateUpdateReviewModalBody
          ref={this.textareaRef}
          auditors={this._ensureTextField(this.props.auditors)}
          isSelfReview={false}
          shouldRenderCommentBox={true}
          reviewAction={null}
          buttonText={submitText}
          error={this.state.error}
          selectedAuditors={this.state.selectedAuditors}
          comment={this.state.message}
          handleCommentChange={this.handleCommentChange}
          handleAuditorChange={this.handleAuditorChange}
        />
      </Modal>
    );
  }
}

export default BulkCreateStageEntryReviewModal;
